<template>
  <div
    :style="{...this.wrapperStyle, ...this.wrapperStyles}"
    v-if="this.geometryData || this.floorSchema"
  >
    <div ref="canvasContainer" class="canvas-container">
      <canvas 
        ref="geometryCanvas" 
        class="geometry-canvas"
        @wheel.prevent="handleCanvasWheel"
        @mousedown="handleCanvasMouseDown"
        @mousemove="handleCanvasMouseMove"
        @mouseup="handleCanvasMouseUp"
        @mouseleave="handleCanvasMouseLeave"
        @click="handleCanvasClick"
      ></canvas>
      <div v-if="showTooltip && hoveredWall" class="geometry-tooltip" :style="{left: tooltipX + 'px', top: tooltipY + 'px'}">
        <div class="text-subtitle2 tw-p-2">
          {{ `${this.deletingMode ? 'Удалить - ' : ''}${hoveredWall?.floor_param_type_name} ${hoveredWall?.floor_param_type_name_relative ? `, ${hoveredWall?.floor_param_type_name_relative}` :''}` }}
          <div
            v-if="hoveredWall?.has_material"
            class="text-caption text-positive"
            style="margin-top: 4px;"
          >
            ✓ Есть материал
          </div>
        </div>
      </div>
    </div>
  </div>
  <q-linear-progress v-else-if="this.schemaFetching" indeterminate />
  <NotFoundText v-else-if="!this.schemaFetching" />
</template>

<script>
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar';
import NotFoundText from "@/components/common/NotFoundText.vue";
import { API_URL, MODAL_NAMES } from '@/constants';
import { deleteFloorMapLine, updateFloorParam } from './services';
import { useAppStore } from '@/services/store/app';
import { mapState } from 'pinia';

export default {
  name: "SchemaRevit",
  components: { NotFoundText },
  props: [
    'floorSchema',
    'floorMapInfo', 
    'activeFloorParamTypeIds',
    'updateParamBody',
    'updateData',
    'editingMode',
    'getFloorSchemaData',
    'activeData', 
    'bufferParams', 
    'getBufferParamsData', 
    'getFloorLegendData', 
    'updateFloorSchema', 
    'schemaFetching', 
    'activeFloorFlatId',
    'flatBorders',
    'params',
    'floorMapId', 
    'deletingMode', 
    'activeFlat', 
    'groupEditingMode'
  ],
  data() {
    return {
      wrapperStyle: {
        position: 'relative',
      },
      tooltipX: 0,
      tooltipY: 0,
      showTooltip: false,
      hoveredWall: null,
      geometryData: [],
      geometryLines: [],
      geometryBounds: null,
      backgroundImage: null,
      canvasZoom: 1,
      canvasOffsetX: 0,
      canvasOffsetY: 0,
      isDragging: false,
      lastMouseX: 0,
      lastMouseY: 0,
      dragStartX: 0,
      dragStartY: 0,
      hasDragged: false,
      activeObjectId: null,
      activeBufferObjectId: null,
      updatingParam: false,
      deleteLoading: false,
      // Калибровка изображения
      imageOffsetX: 0,
      imageOffsetY: 0,
      imageWidth: 0,
      imageHeight: 0,
    };
  },
  computed: {
    ...mapState(useAppStore, ['menuDrawerOpen']),
    wrapperStyles() {
      const view = {
        width: `${(window.innerWidth - 250) || 1170}px`,
        height: `${(window.innerHeight - 205) || 700}px`,
      }
      if(this.menuDrawerOpen)
        view.width = `${window.innerWidth || 1170}px`
      return view
    },
  },
  methods: {
    async loadGeometryData() {
      // Используем lines и circles из floorSchema
      // В будущем, для Revit данных, они должны иметь поле revit_wkt_geom вместо points
      const geometryData = []
      
      // Обрабатываем линии - для Revit у них должно быть поле revit_wkt_geom
      if (this.floorSchema?.lines) {
        this.floorSchema.lines.forEach(item => {
          // Пробуем использовать revit_wkt_geom, если его нет - преобразуем points в WKT
          let wktGeom = item.revit_wkt_geom || item.param_geom
          
          // Если нет ни revit_wkt_geom, ни param_geom, но есть points, пропускаем - это AutoCAD данные
          if (!wktGeom && item.points) {
            return
          }
          
          if (wktGeom) {
            geometryData.push({
              geom: wktGeom,
              floor_param_id: item.floor_param_id,
              floor_param_type_id: item.floor_param_type_id,
              floor_param_type_name: item.floor_param_type_name,
              floor_param_type_name_relative: item.floor_param_type_name_relative,
              floor_param_type_color: item.floor_param_type_color,
              floor_flat_id: item.floor_flat_id,
              room_id: item.room_id,
              has_material: item.has_material,
              floor_buffer_id: item.floor_buffer_id,
            })
          }
        })
      }

      // Обрабатываем circles (точки) - для Revit
      if (this.floorSchema?.circles) {
        this.floorSchema.circles.forEach(item => {
          let wktGeom = item.revit_wkt_geom || item.param_geom
          
          if (!wktGeom && item.points) {
            return
          }
          
          if (wktGeom) {
            geometryData.push({
              geom: wktGeom,
              floor_param_id: item.floor_param_id,
              floor_param_type_id: item.floor_param_type_id,
              floor_param_type_name: item.floor_param_type_name,
              floor_param_type_name_relative: item.floor_param_type_name_relative,
              floor_param_type_color: item.floor_param_type_color,
              floor_flat_id: item.floor_flat_id,
              room_id: item.room_id,
              has_material: item.has_material,
              floor_buffer_id: item.floor_buffer_id,
              coord_type: 'POINT'
            })
          }
        })
      }

      if (geometryData.length === 0) {
        console.warn('SchemaRevit: Нет данных с revit_wkt_geom или param_geom полями')
        return
      }

      this.geometryData = geometryData
      this.$nextTick(() => {
        setTimeout(() => {
          this.prepareGeometry()
          this.drawGeometry()
        }, 100)
      })
    },
    loadBackground() {
      const src = this.floorSchema?.floor_map?.image_url
      if(!src) return

      // Загружаем калибровку из floor_map или floorMapInfo
      const floorMap = this.floorSchema?.floor_map || this.floorMapInfo
      this.imageOffsetX = floorMap?.image_offset_x || 0
      this.imageOffsetY = floorMap?.image_offset_y || 0
      this.imageWidth = floorMap?.image_width || 0
      this.imageHeight = floorMap?.image_height || 0

      const image = new Image();
      image.src = `${API_URL}${this.floorSchema?.floor_map?.image_url}`;
      image.onload = () => {
        this.backgroundImage = image;
        this.drawGeometry();
      };
      image.onerror = () => {
        console.log('Основное изображение не загрузилось, пробуем запасной URL');
        const fallbackImage = new Image();
        fallbackImage.src = `https://master-api.smartremont.kz${this.floorSchema?.floor_map?.image_url}`;
        fallbackImage.onload = () => {
          this.backgroundImage = fallbackImage;
          this.drawGeometry();
        };
        fallbackImage.onerror = () => {
          console.error('Не удалось загрузить изображение схемы этажа с обоих серверов');
        };
      };
    },
    parseMultiLineString(wktString) {
      if (!wktString || !wktString.startsWith('MULTILINESTRING')) {
        return [];
      }

      try {
        let coordsStr = wktString.substring('MULTILINESTRING('.length);
        coordsStr = coordsStr.substring(0, coordsStr.length - 1);

        const lineGroups = [];
        let currentGroup = '';
        let depth = 0;
        
        for (let i = 0; i < coordsStr.length; i++) {
          const char = coordsStr[i];
          
          if (char === '(') {
            depth++;
            if (depth === 1) continue;
          } else if (char === ')') {
            depth--;
            if (depth === 0) {
              if (currentGroup.trim()) {
                lineGroups.push(currentGroup.trim());
              }
              currentGroup = '';
              continue;
            }
          } else if (char === ',' && depth === 0) {
            continue;
          }
          
          if (depth > 0) {
            currentGroup += char;
          }
        }

        const lines = [];

        lineGroups.forEach(group => {
          const points = group.split(',').map(pair => {
            const parts = pair.trim().split(/\s+/);
            const x = parseFloat(parts[0]);
            const y = parseFloat(parts[1]);
            return { x, y };
          }).filter(p => !isNaN(p.x) && !isNaN(p.y));
          
          if (points.length > 0) {
            lines.push(points);
          }
        });

        return lines;
      } catch (error) {
        console.error('Ошибка парсинга WKT:', error);
        return [];
      }
    },
    prepareGeometry() {
      let allPoints = [];
      const geometryLines = [];

      this.geometryData.forEach(item => {
        const wktString = item.geom || item.revit_wkt_geom;
        const lines = this.parseMultiLineString(wktString);

        lines.forEach(line => {
          geometryLines.push({
            points: line,
            ...item
          });
          allPoints = allPoints.concat(line);
        });
      });

      if (allPoints.length === 0) {
        return;
      }

      const minX = Math.min(...allPoints.map(p => p.x));
      const maxX = Math.max(...allPoints.map(p => p.x));
      const minY = Math.min(...allPoints.map(p => p.y));
      const maxY = Math.max(...allPoints.map(p => p.y));

      this.geometryLines = geometryLines;
      this.geometryBounds = { minX, maxX, minY, maxY };
    },
    drawGeometry() {
      const canvas = this.$refs.geometryCanvas;
      const container = this.$refs.canvasContainer;
      
      if (!canvas || !container || !this.geometryBounds) {
        return;
      }

      const ctx = canvas.getContext('2d');
      
      const width = container.clientWidth || 1200;
      const height = container.clientHeight || 800;
      canvas.width = width;
      canvas.height = height;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const { minX, maxX, minY, maxY } = this.geometryBounds;
      const dataWidth = maxX - minX;
      const dataHeight = maxY - minY;

      const padding = 50;
      const availableWidth = canvas.width - 2 * padding;
      const availableHeight = canvas.height - 2 * padding;

      let scaleX = availableWidth / dataWidth;
      let scaleY = availableHeight / dataHeight;
      let baseScale = Math.min(scaleX, scaleY);

      const scale = baseScale * this.canvasZoom;

      const scaledWidth = dataWidth * scale;
      const scaledHeight = dataHeight * scale;
      const offsetX = (canvas.width - scaledWidth) / 2 + this.canvasOffsetX;
      const offsetY = (canvas.height - scaledHeight) / 2 + this.canvasOffsetY;

      const transformX = (x) => offsetX + (x - minX) * scale;
      const transformY = (y) => offsetY + (maxY - y) * scale;

      this.geometryBounds.scale = scale;
      this.geometryBounds.padding = padding;
      this.geometryBounds.offsetX = offsetX;
      this.geometryBounds.offsetY = offsetY;
      this.geometryBounds.transformX = transformX;
      this.geometryBounds.transformY = transformY;

      // Рисуем линии сначала
      this.geometryLines.forEach((geom, index) => {
        // Проверяем фильтры
        if (!this.shouldDrawLine(geom)) {
          return;
        }

        const isHovered = this.hoveredWall && 
          ((geom.floor_param_id && this.hoveredWall.floor_param_id === geom.floor_param_id) ||
           (geom.floor_buffer_id && this.hoveredWall.floor_buffer_id === geom.floor_buffer_id));

        ctx.beginPath();
        ctx.strokeStyle = this.getLineColor(geom, index);
        ctx.lineWidth = this.getLineWidth(geom, isHovered);

        // Если линия под курсором, добавляем эффект свечения
        if (isHovered) {
          ctx.shadowColor = this.getLineColor(geom, index);
          ctx.shadowBlur = 10;
          ctx.lineWidth = this.getLineWidth(geom, isHovered) + 2;
        } else {
          ctx.shadowBlur = 0;
        }

        geom.points.forEach((point, i) => {
          const x = transformX(point.x);
          const y = transformY(point.y);

          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });

        ctx.stroke();
        
        // Сбрасываем тень для следующих линий
        ctx.shadowBlur = 0;
      });

      // Рисуем изображение ПОВЕРХ линий с использованием калибровки
      if (this.backgroundImage) {
        ctx.save();
        ctx.globalAlpha = 0.5;
        
        // Если есть калибровка - используем её
        if (this.imageWidth > 0 && this.imageHeight > 0) {
          const imageCanvasX = transformX(this.imageOffsetX);
          const imageCanvasY = transformY(this.imageOffsetY + this.imageHeight);
          const imageCanvasWidth = this.imageWidth * scale;
          const imageCanvasHeight = this.imageHeight * scale;
          
          ctx.drawImage(this.backgroundImage, imageCanvasX, imageCanvasY, imageCanvasWidth, imageCanvasHeight);
        } else {
          // Если калибровки нет - рисуем по всей геометрии
          const imgX = transformX(minX);
          const imgY = transformY(maxY);
          const imgWidth = scaledWidth;
          const imgHeight = scaledHeight;
          ctx.drawImage(this.backgroundImage, imgX, imgY, imgWidth, imgHeight);
        }
        
        ctx.restore();
      }
    },
    shouldDrawLine(geom) {
      if (!this.editingMode) {
        if (this.activeFloorParamTypeIds?.length) {
          const isExist = this.activeFloorParamTypeIds.includes(geom?.floor_param_type_id)
          if (!isExist) return false
        }
        if (this.activeFloorFlatId) {
          if (geom?.floor_flat_id !== this.activeFloorFlatId) return false
        }
        if (this.updateParamBody?.room_id) {
          if (geom?.room_id !== this.updateParamBody?.room_id) return false
        }
      }
      return true
    },
    getLineColor(geom, index) {
      if (this.activeObjectId === geom.floor_param_id && !!geom.floor_param_id) return '#970e0e'
      if (!geom.floor_param_id && this.activeBufferObjectId === geom.floor_buffer_id) return '#970e0e'
      if (!geom.floor_param_id) return '#000'
      if (this.flatBorders && this.floorSchema?.flatColors) return this.floorSchema?.flatColors[geom.floor_flat_id]
      
      return geom?.floor_param_type_color || '#000'
    },
    getLineWidth(geom, isHovered = false) {
      let width = 2
      if (!this.editingMode) {
        if (this.activeFloorParamTypeIds?.length) {
          const isExist = this.activeFloorParamTypeIds.includes(geom?.floor_param_type_id)
          if (!isExist) width = 0
        }
        if (this.activeFloorFlatId) {
          if (geom?.floor_flat_id !== this.activeFloorFlatId) width = 0
        }
        if (this.updateParamBody?.room_id) {
          if (geom?.room_id !== this.updateParamBody?.room_id) width = 0
        }
      }
      
      // Увеличиваем толщину линии при наведении
      if (isHovered && width > 0) {
        width = width * 2.5;
      }
      
      return width
    },
    handleCanvasWheel(e) {
      e.preventDefault();
      const delta = e.deltaY;
      const zoomFactor = delta > 0 ? 0.9 : 1.1;
      
      this.canvasZoom = Math.max(0.1, Math.min(10, this.canvasZoom * zoomFactor));
      this.drawGeometry();
    },
    handleCanvasMouseDown(e) {
      this.isDragging = true;
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.hasDragged = false;
    },
    handleCanvasMouseMove(e) {
      if (this.isDragging) {
        const dx = e.clientX - this.lastMouseX;
        const dy = e.clientY - this.lastMouseY;
        
        this.canvasOffsetX += dx;
        this.canvasOffsetY += dy;
        
        this.lastMouseX = e.clientX;
        this.lastMouseY = e.clientY;
        
        // Проверяем, было ли значительное перемещение (больше 5 пикселей)
        const totalDx = Math.abs(e.clientX - this.dragStartX);
        const totalDy = Math.abs(e.clientY - this.dragStartY);
        if (totalDx > 5 || totalDy > 5) {
          this.hasDragged = true;
        }
        
        this.drawGeometry();
      } else {
        // Проверяем наведение на линию
        this.checkHover(e);
      }
    },
    handleCanvasMouseUp() {
      this.isDragging = false;
    },
    handleCanvasMouseLeave() {
      this.isDragging = false;
      this.hasDragged = false;
      this.showTooltip = false;
      this.hoveredWall = null;
    },
    handleCanvasClick(e) {
      // Если было перемещение, не обрабатываем клик
      if (this.hasDragged) {
        this.hasDragged = false;
        return;
      }

      const canvas = this.$refs.geometryCanvas;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const clickedLine = this.findLineAtPoint(x, y);
      
      if (clickedLine) {
        this.handleLineClick(clickedLine);
      }
    },
    checkHover(e) {
      const canvas = this.$refs.geometryCanvas;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const hoveredLine = this.findLineAtPoint(x, y);
      const previousHoveredWall = this.hoveredWall;
      
      if (hoveredLine) {
        this.showTooltip = true;
        this.hoveredWall = hoveredLine;
        this.tooltipX = e.clientX + 10;
        this.tooltipY = e.clientY + 10;
        canvas.style.cursor = 'pointer';
      } else {
        this.showTooltip = false;
        this.hoveredWall = null;
        canvas.style.cursor = 'default';
      }

      // Перерисовываем canvas если изменилась наведенная линия
      const hoveredWallChanged = 
        (!previousHoveredWall && hoveredLine) ||
        (previousHoveredWall && !hoveredLine) ||
        (previousHoveredWall && hoveredLine && 
         ((previousHoveredWall.floor_param_id !== hoveredLine.floor_param_id) ||
          (previousHoveredWall.floor_buffer_id !== hoveredLine.floor_buffer_id)));

      if (hoveredWallChanged) {
        this.drawGeometry();
      }
    },
    findLineAtPoint(x, y) {
      if (!this.geometryBounds || !this.geometryBounds.transformX) return null;

      const threshold = 10; // Пиксели для определения попадания

      for (let i = this.geometryLines.length - 1; i >= 0; i--) {
        const geom = this.geometryLines[i];
        
        if (!this.shouldDrawLine(geom)) continue;

        const points = geom.points;
        for (let j = 0; j < points.length - 1; j++) {
          const x1 = this.geometryBounds.transformX(points[j].x);
          const y1 = this.geometryBounds.transformY(points[j].y);
          const x2 = this.geometryBounds.transformX(points[j + 1].x);
          const y2 = this.geometryBounds.transformY(points[j + 1].y);

          const distance = this.distanceToSegment(x, y, x1, y1, x2, y2);
          
          if (distance < threshold) {
            return geom;
          }
        }
      }
      
      return null;
    },
    distanceToSegment(px, py, x1, y1, x2, y2) {
      const A = px - x1;
      const B = py - y1;
      const C = x2 - x1;
      const D = y2 - y1;

      const dot = A * C + B * D;
      const lenSq = C * C + D * D;
      let param = -1;

      if (lenSq !== 0) {
        param = dot / lenSq;
      }

      let xx, yy;

      if (param < 0) {
        xx = x1;
        yy = y1;
      } else if (param > 1) {
        xx = x2;
        yy = y2;
      } else {
        xx = x1 + param * C;
        yy = y1 + param * D;
      }

      const dx = px - xx;
      const dy = py - yy;
      return Math.sqrt(dx * dx + dy * dy);
    },
    async handleLineClick(wall) {
      if (!wall) return
      if (!this.editingMode) {
        if (wall.floor_param_id)
          this.activeObjectId = wall.floor_param_id
        else
          this.activeBufferObjectId = wall.floor_buffer_id
      }
      if (this.deletingMode) {
        this.deleteObject(wall)
        return
      }
      this.editingMode ? this.updateFloorParamData(wall) : this.showObjectInfoModal(wall)
    },
    showObjectInfoModal(wall) {
      if (!wall?.floor_param_id) {
        this.activeObjectId = null
        this.activeBufferObjectId = null
        return
      }
      this.showModal(MODAL_NAMES.FLOOR_SCHEMA_OBJECT_INFO, {
        title: `${wall?.floor_param_type_name || 'Информация об объекте'} (ID: ${wall?.floor_param_id})`,
        canChooseMaterial: this.floorMapInfo?.can_choose_material,
        floorMapId: this.floorMapId,
        data: wall, 
        params: {floor_map_id: this.floorMapId, project_type_id: this.activeData.project_type_id}, 
        resident_id: this.activeData?.resident_id,
        onClose: () => {
          this.activeObjectId = null
          this.activeBufferObjectId = null
        },
        onSubmit: this.onUpdateObject,
      })
    },
    onUpdateObject() {
      this.getFloorLegendData()
      this.getFloorSchemaData(this.floorMapId)
      this.updateData && this.updateData()
    },
    async updateFloorParamData(wall) {
      const { floor_param_id, floor_buffer_id } = wall
      const body = {
        ...this.updateParamBody, 
        floor_param_id, 
        floor_buffer_id, 
        floor_flat_id: this.activeFlat?.floor_flat_id,
        with_relative: !!this.groupEditingMode
      }

      this.updatingParam = true
      const res = await updateFloorParam(body, {floor_map_id: this.floorMapId})
      this.updatingParam = false
      if (!res) return
      this.getFloorLegendData()
      this.updateFloorSchema(res)
      this.showSucceedSnackbar()
      if (floor_buffer_id && this.bufferParams) {
        if (wall.coord_type === 'POINT') {
          this.bufferParams.circles = this.bufferParams?.circles?.filter(item => item.floor_buffer_id !== floor_buffer_id)
        } else {
          this.bufferParams.lines = this.bufferParams?.lines?.filter(item => item.floor_buffer_id !== floor_buffer_id)
        }
        if (this.groupEditingMode)
          return this.getBufferParamsData()
      }
    },
    async deleteObject(wall) {
      const { floor_param_id } = wall
      this.deleteLoading = true;
      const res = await deleteFloorMapLine(floor_param_id, {floor_map_id: this.floorMapId})
      this.deleteLoading = false;
      if (!res) return
      this.updateFloorSchema(res)
      this.getFloorLegendData()
      this.showSucceedSnackbar()
    },
  },
  watch: {
    floorSchema: {
      handler() {
        this.loadBackground()
        this.loadGeometryData()
      },
      deep: true
    },
    activeFloorParamTypeIds() {
      this.drawGeometry()
    },
    activeFloorFlatId() {
      this.drawGeometry()
    },
    'updateParamBody.room_id'() {
      this.drawGeometry()
    },
  },
  mounted() {
    this.loadBackground();
    this.loadGeometryData();
    
    window.addEventListener('resize', this.drawGeometry);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.drawGeometry);
  },
  setup() {
    const {showModal} = useAppStore()
    const {showSucceedSnackbar} = useCustomSnackbar()
    return {showSucceedSnackbar, showModal}
  }
};
</script>

<style scoped lang="scss">
.canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.geometry-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.geometry-tooltip {
  position: absolute;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  pointer-events: none;
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>

